import { NavLink, useNavigate } from "react-router-dom";
import { useState } from "react";
import { LayoutDashboard, ClipboardList, FileCheck2, ShieldAlert, ListChecks, MapPin, SlidersHorizontal, Building, Building2, Sun, Moon, LogOut, RotateCcw, Menu, X } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useApp } from "@/context/AppContext";
import NotificationsBell from "@/components/NotificationsBell";
import { Dialog, DialogContent, DialogHeader, DialogFooter, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import api from "@/lib/api";
import { toast } from "sonner";

const LINKS = [
  { to: "/", icon: LayoutDashboard, label: "Tableau de bord", end: true },
  { to: "/operations", icon: ClipboardList, label: "Opérations" },
  { to: "/autorisations", icon: FileCheck2, label: "Autorisations" },
  { to: "/coactivite", icon: ShieldAlert, label: "Coactivité" },
  { to: "/actions", icon: ListChecks, label: "Plan d'actions" },
  { to: "/sites", icon: Building, label: "Sites & Entreprises" },
  { to: "/zones", icon: MapPin, label: "Zones" },
  { to: "/organisation", icon: Building2, label: "Organisation" },
  { to: "/regles", icon: SlidersHorizontal, label: "Règles" },
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const { sites, selectedSite, setSelectedSite, theme, toggleTheme, loadRef } = useApp();
  const nav = useNavigate();
  const [resetOpen, setResetOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  const resetDemo = async () => {
    setResetOpen(false);
    try {
      await api.post("/reset-demo");
      await loadRef();
      toast.success("Démo réinitialisée");
      nav("/");
    } catch (e) { toast.error("Échec de la réinitialisation"); }
  };

  return (
    <div className="shell">
      <div className={`nav-overlay ${menuOpen ? "open" : ""}`} onClick={() => setMenuOpen(false)} data-testid="nav-overlay" />
      <nav className={`nav ${menuOpen ? "open" : ""}`} aria-label="Navigation principale">
        <div className="logo">PERMIT<em>&amp;</em>CO</div>
        <div className="logo-sub">Pilotage Plans de Prévention</div>
        <div className="nav-links" onClick={() => setMenuOpen(false)}>
          {LINKS.map((l) => (
            <NavLink key={l.to} to={l.to} end={l.end} data-testid={`nav-${l.label.toLowerCase().replace(/[^a-z]/g, "")}`}
              className={({ isActive }) => (isActive ? "on" : "")}>
              <l.icon size={15} />{l.label}
            </NavLink>
          ))}
        </div>
        <div className="nav-foot">
          {["PERMITCO", "NOVASITE"].includes(user?.tenantId) && (
            <button className="btn-mini" onClick={() => setResetOpen(true)} data-testid="reset-demo-btn"><RotateCcw size={12} />Réinitialiser la démo</button>
          )}
        </div>
      </nav>

      <Dialog open={resetOpen} onOpenChange={setResetOpen}>
        <DialogContent data-testid="reset-demo-dialog">
          <DialogHeader>
            <DialogTitle>Réinitialiser les données de démonstration ?</DialogTitle>
            <DialogDescription>
              Cette action restaure le jeu de données de démonstration. Les modifications de démo en cours seront écrasées.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter style={{ marginTop: 12, gap: 8 }}>
            <button className="btn btn-g" data-testid="reset-demo-cancel" onClick={() => setResetOpen(false)}>Annuler</button>
            <button className="btn btn-p" data-testid="reset-demo-confirm" onClick={resetDemo}>Réinitialiser</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <div>
        <div className="topbar">
          <button className="iconbtn nav-toggle" onClick={() => setMenuOpen((v) => !v)} data-testid="nav-toggle" aria-label="Menu">
            {menuOpen ? <X size={18} /> : <Menu size={18} />}
          </button>
          <select className="site-select" data-testid="site-selector" value={selectedSite}
            onChange={(e) => setSelectedSite(e.target.value)}>
            <option value="all">Tous les sites</option>
            {sites.map((s) => <option key={s.id} value={s.id}>{s.nom}</option>)}
          </select>
          <div className="spacer" />
          <NotificationsBell />
          <button className="iconbtn" onClick={toggleTheme} data-testid="theme-toggle" aria-label="Basculer thème">
            {theme === "light" ? <Moon size={16} /> : <Sun size={16} />}
          </button>
          <div className="userchip">
            <span className="n">{user?.name}</span>
            <span className="r">{user?.role}</span>
          </div>
          <button className="iconbtn" onClick={() => { logout(); }} data-testid="logout-btn" aria-label="Déconnexion"><LogOut size={16} /></button>
        </div>
        <main className="main" id="app">{children}</main>
      </div>
    </div>
  );
}
